import { type JSX, type MouseEvent } from 'react';

import { DataList, EloBadge, EloTag } from '@plurall/elo-v3';
import { DateTime } from 'luxon';

import { ActivityCard } from '@/components';
import { STAGE_DISPLAY_NAMES } from '@/features/learning-path/lib/constants';
import { type LearningPathsListItem } from '@/features/learning-path/schemas/learningPathsList.schema';
import { type StageStatus } from '@/features/learning-path/schemas/stage.schema';
import { formatDate } from '@/lib/formatters';
import { isDefined } from '@/lib/validators/isDefined';

type Props = {
  data: LearningPathsListItem;
  /** When set, the title becomes a link to the learning path. */
  href?: string;
  /** Lets in-app routes navigate without a full page reload. */
  onTitleClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export function LearningPathCard({ data, href, onTitleClick }: Props): JSX.Element {
  const {
    tituloTema: title,
    imagemUrl: imageUrl,
    generoTextual: genre,
    qtdTurmas: schoolClassCount,
    qtdAlunos: studentCount,
    etapas,
  } = data;

  const now = DateTime.now();
  const stages = etapas.map((etapa) => {
    const start = DateTime.fromISO(etapa.dataHoraInicio);
    const end = DateTime.fromISO(etapa.dataHoraEncerramento);

    const status: StageStatus =
      start.isValid && end.isValid ? (end < now ? 'past' : start <= now ? 'current' : 'upcoming') : 'upcoming';

    return {
      stageKey: etapa.etapa,
      nome: STAGE_DISPLAY_NAMES[etapa.etapa],
      dataInicio: start.isValid ? (start.toISODate() ?? '') : '',
      dataFim: end.isValid ? (end.toISODate() ?? '') : '',
      status,
    };
  });

  const currentStageIndex = stages.findIndex((stage) => stage.status === 'current');
  const hasCurrentStage = currentStageIndex >= 0;
  const currentStage = stages[currentStageIndex] ?? null;

  const stageCountLabel = hasCurrentStage
    ? `Etapa ${currentStageIndex + 1} de ${stages.length}`
    : `Trilha de ${stages.length} ${stages.length === 1 ? 'etapa' : 'etapas'}`;

  return (
    <ActivityCard
      heroSrc={imageUrl}
      heroAlt={title}
      topRightSlot={
        <EloBadge
          variant='information'
          label={stageCountLabel}
        />
      }
      bottomLeftSlot={
        <>
          {/* TODO: replace icon 'globe' with 'map-open' when it becomes available in Elo */}
          <EloTag.Root
            type='read-only'
            size='xsmall'
            variant='mint'
            emphasis='low'
            icon='globe'
            label='Atividade de trilha'
          />
          {currentStage !== null && (
            <EloTag.Root
              type='read-only'
              size='xsmall'
              variant='white'
              emphasis='low'
              label={currentStage.nome}
            />
          )}
        </>
      }
      title={
        href ? (
          <ActivityCard.TitleLink
            href={href}
            onClick={onTitleClick}>
            {title}
          </ActivityCard.TitleLink>
        ) : (
          title
        )
      }>
      <ActivityCard.Meta>
        {(schoolClassCount > 0 || studentCount > 0) && (
          <ActivityCard.MetaItem>
            <ActivityCard.MetaIcon
              icon='users'
              size='small'
              color='foreground.tertiary'
            />
            <ActivityCard.MetaItemContent>
              <ActivityCard.MetaText>
                {schoolClassCount} {schoolClassCount === 1 ? 'turma' : 'turmas'}, {studentCount} alunos
              </ActivityCard.MetaText>
            </ActivityCard.MetaItemContent>
          </ActivityCard.MetaItem>
        )}

        {isDefined(genre) && (
          <ActivityCard.MetaItem>
            <ActivityCard.MetaIcon
              icon='pen'
              size='small'
              color='foreground.tertiary'
            />
            <ActivityCard.MetaItemContent>
              <ActivityCard.MetaText>{genre}</ActivityCard.MetaText>
            </ActivityCard.MetaItemContent>
          </ActivityCard.MetaItem>
        )}

        {stages.length > 0 && (
          <ActivityCard.MetaItem>
            {/* TODO: replace icon 'globe' with 'map-open' when it becomes available in Elo */}
            <ActivityCard.MetaIcon
              icon='globe'
              size='small'
              color='foreground.tertiary'
            />
            <ActivityCard.MetaItemContent>
              <ActivityCard.MetaText>Etapas da trilha</ActivityCard.MetaText>
              <ActivityCard.MetaItemDescription>
                <DataList.Root
                  orientation='horizontal'
                  gap='gap.3xsmall'
                  color='text.tertiary'>
                  {stages.map((stage) => (
                    <DataList.Item
                      key={stage.stageKey}
                      textDecoration={stage.status === 'past' ? 'line-through' : undefined}>
                      <DataList.ItemLabel textStyle='paragraph.small.semibold'>{stage.nome}:</DataList.ItemLabel>
                      <DataList.ItemValue
                        textStyle='paragraph.small.regular'
                        justifyContent='end'>
                        {stage.dataInicio && stage.dataFim
                          ? formatDate(stage.dataInicio, 'dd/MM') + ' - ' + formatDate(stage.dataFim, 'dd/MM')
                          : '—'}
                      </DataList.ItemValue>
                    </DataList.Item>
                  ))}
                </DataList.Root>
              </ActivityCard.MetaItemDescription>
            </ActivityCard.MetaItemContent>
          </ActivityCard.MetaItem>
        )}
      </ActivityCard.Meta>
    </ActivityCard>
  );
}
